import { useEffect, useState } from "react";
import { axiosInstance } from "./networking";

/**
 * Project governance (IC_Ticket project TD), served through Thrive's server
 * (`thrive_server/apps/ic_ticket/ic_governance_router.ts`, mounted at
 * `/ic-ticket/governance`). Admins only. The browser only ever talks to
 * Thrive; people are names only — IC_Ticket e-mail addresses and user ids
 * never reach the browser. Contract: thrive_server/claude_doc/governance/CONTRACT.md.
 */

export type GovKind = "ISSUE" | "TASK" | "CHANGE_REQUEST" | "RELEASE" | "UAT_TEST";
export const GOV_KINDS: GovKind[] = ["ISSUE", "TASK", "CHANGE_REQUEST", "RELEASE", "UAT_TEST"];

export type Party = "CLIENT" | "VENDOR";
export type GovPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export const GOV_PRIORITIES: GovPriority[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

export type IssueStatus =
  | "NEW"
  | "TRIAGED"
  | "IN_PROGRESS"
  | "WAITING"
  | "FIXED"
  | "RETEST"
  | "CLOSED"
  | "REJECTED";
export type TaskStatus = "NOT_STARTED" | "IN_PROGRESS" | "BLOCKED" | "COMPLETE" | "SIGNED_OFF";
export type ChangeRequestStatus =
  | "SUBMITTED"
  | "ASSESSING"
  | "PENDING_DECISION"
  | "APPROVED"
  | "REJECTED"
  | "DEFERRED"
  | "SCHEDULED"
  | "IMPLEMENTED";
export type ReleaseStatus =
  | "PLANNED"
  | "IN_DEV"
  | "QA"
  | "UAT"
  | "APPROVED"
  | "DEPLOYED"
  | "VALIDATED"
  | "ROLLED_BACK";
export type UatStatus = "NOT_RUN" | "PASS" | "FAIL" | "BLOCKED";

export type SignOffStatus = "NONE" | "PENDING" | "APPROVED" | "REJECTED";
export type DecisionOutcome = "APPROVED" | "REJECTED" | "DEFERRED";
export type Environment = "DEV" | "QA" | "UAT" | "PROD";
export type Severity = "S1" | "S2" | "S3" | "S4";
export type ReleaseType = "MAJOR" | "MINOR" | "PATCH" | "HOTFIX";
export type RetestResult = "PASS" | "FAIL";
export type UatResult = "PASS" | "FAIL" | "BLOCKED";
export type LinkRelation =
  | "DEPENDS_ON"
  | "BLOCKS"
  | "RELATED"
  | "FIXED_IN"
  | "FIXES"
  | "TESTED_IN"
  | "TESTS";

export const ENVIRONMENTS: Environment[] = ["DEV", "QA", "UAT", "PROD"];
export const SEVERITIES: Severity[] = ["S1", "S2", "S3", "S4"];

// ---------------------------------------------------------------------------
// Settings (GET /me)
// ---------------------------------------------------------------------------

export type SignOffModule = "tasks" | "changeRequests" | "releases" | "uat" | "weeklyStatus";

export interface GovSettingsOn {
  enabled: true;
  projectKey: string;
  modules: { issues: true } & Record<SignOffModule, boolean>;
  signOffRequired: Record<SignOffModule, boolean>;
  parties: { clientLabel: string; vendorLabel: string };
  workstreams: string[];
}
export interface GovSettingsOff {
  enabled: false;
  /** `not_manager` = the caller's role; `not_configured` = IC_TICKET_API_KEY unset on the server. */
  reason?: "not_manager" | "not_configured";
}
export type GovSettings = GovSettingsOn | GovSettingsOff;

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export interface SignOffView {
  status: SignOffStatus;
  requestedBy: string | null;
  requestedAt: string | null;
  requestComment: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  comment: string | null;
}

export interface EvidenceView {
  label: string;
  url: string;
  source: string | null;
  at: string | null;
}

export interface LinkTarget {
  id: string;
  displayId: string | null;
  kind: GovKind;
  title: string;
  status: string;
}

export interface LinkView {
  targetId: string;
  relation: LinkRelation;
  targetDisplayId: string | null;
  targetKind: GovKind | null;
  /** Only on item detail. */
  target?: LinkTarget | null;
}

export interface ReleaseRef {
  id: string;
  displayId: string | null;
  title: string;
  version: string | null;
  status: string;
}

export interface GovItemBase {
  id: string;
  displayId: string;
  kind: GovKind;
  title: string;
  description: string;
  /** The kind's stored status (see governanceStatus.tsx for labels). */
  status: string;
  priority: GovPriority;
  /** Whose turn it is. */
  leadParty: Party | null;
  clientOwner: string | null;
  vendorOwner: string | null;
  assignedTo: string | null;
  createdBy: string;
  /** The caller raised it (server compares e-mails; the e-mail itself never reaches the browser). */
  createdByMe: boolean;
  /** Date-only (00:00 UTC) — show with dateOnly(). */
  dueDate: string | null;
  /** Minutes logged. */
  actualMinutes: number | null;
  signOff: SignOffView;
  evidence: EvidenceView[];
  links: LinkView[];
  createdAt: string;
  updatedAt: string;
  /** List rows only. */
  commentCount?: number;
  /** List rows only: when the item last entered a terminal status. */
  closedAt?: string | null;
}

export interface GovIssue extends GovItemBase {
  kind: "ISSUE";
  status: IssueStatus;
  reportedAt: string | null;
  environment: Environment | null;
  severity: Severity | null;
  stepsToReproduce: string | null;
  expectedResult: string | null;
  actualResult: string | null;
  businessImpact: string | null;
  resolution: string | null;
  retest: { by: string | null; result: RetestResult | null; at: string | null };
  /** Set while WAITING; `CLARIFICATION` = the team is waiting on the client's answer. */
  waitingReason: string | null;
}

export interface GovTask extends GovItemBase {
  kind: "TASK";
  status: TaskStatus;
  workstream: string | null;
  acceptanceCriteria: string | null;
  startDate: string | null;
  percentComplete: number;
  estimatedMinutes: number | null;
}

export interface GovChangeRequest extends GovItemBase {
  kind: "CHANGE_REQUEST";
  status: ChangeRequestStatus;
  requestedBy: string | null;
  requestedAt: string | null;
  detailedRequest: string | null;
  businessReason: string | null;
  impactAssessment: string | null;
  estimatedMinutes: number | null;
  scheduleImpact: string | null;
  decision: {
    outcome: DecisionOutcome | null;
    by: string | null;
    at: string | null;
    comment: string | null;
  };
  targetRelease: ReleaseRef | null;
}

export interface ReleaseMilestones {
  devComplete: string | null;
  qaComplete: string | null;
  uatStart: string | null;
  uatComplete: string | null;
  production: string | null;
}

export interface ReleaseSummary {
  changeRequests: { total: number; byStatus: Record<string, number> };
  issues: { total: number; byStatus: Record<string, number> };
  uat: { total: number; pass: number; fail: number; blocked: number; notRun: number };
}

export interface GovRelease extends GovItemBase {
  kind: "RELEASE";
  status: ReleaseStatus;
  version: string | null;
  releaseType: ReleaseType | null;
  milestones: ReleaseMilestones;
  expectedDowntime: string | null;
  userCommunication: string | null;
  rollbackPlan: string | null;
  postDeployValidation: string | null;
  deployedAt: string | null;
  /** Item detail only. */
  releaseSummary?: ReleaseSummary;
}

export interface GovUatTest extends GovItemBase {
  kind: "UAT_TEST";
  /** The result; NOT_RUN until one is recorded. */
  status: UatStatus;
  releaseId: string | null;
  release: ReleaseRef | null;
  requirement: string | null;
  scenario: string | null;
  preconditions: string | null;
  expectedResult: string | null;
  actualResult: string | null;
  testDate: string | null;
  retestRequired: boolean;
  retestResult: RetestResult | null;
}

export type GovItem = GovIssue | GovTask | GovChangeRequest | GovRelease | GovUatTest;

export interface GovComment {
  id: string;
  content: string;
  createdAt: string;
  author: string;
}

/** One history line, sentence built by Thrive's server: "{actor} {text}". */
export interface GovHistoryEntry {
  id: string;
  actor: string;
  text: string;
  at: string;
}

/** The only permission flags a client-party user can have true. */
export interface GovPermissions {
  comment: boolean;
  /** Target statuses the admin may move an issue to right now. */
  transitions: string[];
  /** Change request in PENDING_DECISION. */
  decide: boolean;
  /** UAT test: record a result. */
  recordResult: boolean;
}

export type GovItemDetail = GovItem & {
  signOffEnabled: boolean;
  canDecideSignOff: boolean;
  permissions: GovPermissions;
  comments: GovComment[];
  history: GovHistoryEntry[];
  /** Only on the answer to a FAIL result with createIssue. */
  createdIssue?: { id: string; displayId: string | null } | null;
};

export interface GovItemPage {
  items: GovItem[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Weekly reports
// ---------------------------------------------------------------------------

export type ReportStatus = "PUBLISHED" | "ACKNOWLEDGED";
export type OverallStatus = "GREEN" | "AMBER" | "RED";

export interface GovReportSummary {
  id: string;
  /** Date-only — show with dateOnly(). */
  weekEnding: string;
  status: ReportStatus;
  overallStatus: OverallStatus;
  leadParty: Party | null;
  counts: { completed: number; planned: number; decisions: number; risks: number };
  publishedBy: string | null;
  publishedAt: string | null;
  acknowledgement: { by: string | null; at: string | null; comment: string | null };
  acknowledgementRequired: boolean;
}

export interface ReportLine {
  text: string;
  itemId: string | null;
  displayId: string | null;
}
export interface ReportDecision extends ReportLine {
  owner: string | null;
  dueDate: string | null;
}
export interface ReportRisk extends ReportLine {
  mitigation: string | null;
  ownerParty: Party | null;
}

export interface GovReport extends GovReportSummary {
  summary: string;
  completed: ReportLine[];
  planned: ReportLine[];
  decisions: ReportDecision[];
  risks: ReportRisk[];
  permissions: { acknowledge: boolean };
}

export interface GovReportPage {
  items: GovReportSummary[];
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export interface NewChangeRequestInput {
  kind: "CHANGE_REQUEST";
  title: string;
  description: string;
  detailedRequest?: string;
  businessReason?: string;
  priority?: GovPriority;
  /** YYYY-MM-DD */
  dueDate?: string;
}
export interface NewUatTestInput {
  kind: "UAT_TEST";
  /** A release's ObjectId (GovRelease.id). */
  releaseId: string;
  requirement: string;
  scenario: string;
  preconditions?: string;
  expectedResult?: string;
}
export interface NewIssueInput {
  kind: "ISSUE";
  title: string;
  description: string;
  priority?: GovPriority;
  environment?: Environment;
  severity?: Severity;
  stepsToReproduce?: string;
  expectedResult?: string;
  actualResult?: string;
  businessImpact?: string;
}
export type NewItemInput = NewChangeRequestInput | NewUatTestInput | NewIssueInput;

export interface RecordResultInput {
  result: UatResult;
  actualResult?: string;
  /** YYYY-MM-DD, default today. */
  testDate?: string;
  /** Only with FAIL. */
  createIssue?: { title?: string; severity?: Severity; environment?: Environment };
}

export interface ListItemsOptions {
  kind: GovKind;
  /** Comma list of stored statuses. */
  status?: string;
  q?: string;
  /** `createdAt|updatedAt|title|status|priority` (IC_Ticket has no dueDate sort), `-` prefix = descending. */
  sort?: string;
  /** Release ObjectId (UAT tests of one release). */
  release?: string;
  cursor?: string | null;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const BASE = "/ic-ticket/governance";
const itemPath = (id: string) => `${BASE}/items/${encodeURIComponent(id)}`;
const reportPath = (id: string) => `${BASE}/reports/${encodeURIComponent(id)}`;

/** A fresh UUID for an Idempotency-Key header (retries never duplicate). */
export const idempotencyKey = (): string => {
  const c: any = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const OFF_REASONS = ["not_manager", "not_configured"];

export const getGovernanceSettings = (): Promise<GovSettings> =>
  axiosInstance.get(`${BASE}/me`).then((r) => {
    if (r.data?.enabled) return r.data;
    // Keep the reason: the Support page tells an admin when the key is missing.
    const reason = OFF_REASONS.includes(r.data?.reason) ? r.data.reason : undefined;
    return reason ? { enabled: false, reason } : { enabled: false };
  });

export const getItems = (opts: ListItemsOptions): Promise<GovItemPage> =>
  axiosInstance
    .get(`${BASE}/items`, {
      params: {
        kind: opts.kind,
        ...(opts.status ? { status: opts.status } : {}),
        ...(opts.q ? { q: opts.q } : {}),
        ...(opts.sort ? { sort: opts.sort } : {}),
        ...(opts.release ? { release: opts.release } : {}),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
      },
    })
    .then((r) => r.data);

export const getItem = (id: string): Promise<GovItemDetail> =>
  axiosInstance.get(itemPath(id)).then((r) => r.data);

export const createItem = (body: NewItemInput): Promise<GovItemDetail> =>
  axiosInstance
    .post(`${BASE}/items`, body, { headers: { "Idempotency-Key": idempotencyKey() } })
    .then((r) => r.data);

export const addItemComment = (id: string, content: string): Promise<GovComment> =>
  axiosInstance
    .post(`${itemPath(id)}/comments`, { content }, { headers: { "Idempotency-Key": idempotencyKey() } })
    .then((r) => r.data);

export const transitionItem = (
  id: string,
  status: "CLOSED" | "IN_PROGRESS" | "NEW",
  comment?: string,
): Promise<GovItemDetail> =>
  axiosInstance
    .post(`${itemPath(id)}/transition`, { status, ...(comment ? { comment } : {}) })
    .then((r) => r.data);

export const approveSignOff = (id: string, comment?: string): Promise<GovItemDetail> =>
  axiosInstance
    .post(`${itemPath(id)}/signoff/approve`, comment ? { comment } : {})
    .then((r) => r.data);

export const rejectSignOff = (id: string, comment: string): Promise<GovItemDetail> =>
  axiosInstance.post(`${itemPath(id)}/signoff/reject`, { comment }).then((r) => r.data);

export const decideChangeRequest = (
  id: string,
  outcome: DecisionOutcome,
  comment?: string,
): Promise<GovItemDetail> =>
  axiosInstance
    .post(`${itemPath(id)}/decision`, { outcome, ...(comment ? { comment } : {}) })
    .then((r) => r.data);

export const recordUatResult = (id: string, body: RecordResultInput): Promise<GovItemDetail> =>
  axiosInstance.post(`${itemPath(id)}/result`, body).then((r) => r.data);

export const getReports = (): Promise<GovReportPage> =>
  axiosInstance.get(`${BASE}/reports`).then((r) => r.data);

export const getReport = (id: string): Promise<GovReport> =>
  axiosInstance.get(reportPath(id)).then((r) => r.data);

export const acknowledgeReport = (id: string, comment?: string): Promise<GovReport> =>
  axiosInstance
    .post(`${reportPath(id)}/acknowledge`, comment ? { comment } : {})
    .then((r) => r.data);

/** URL of the spreadsheet download for one register (opened by the browser). */
export const exportUrl = (kind: GovKind, format: "xlsx" | "csv" = "xlsx"): string =>
  `${BASE}/export?kind=${encodeURIComponent(kind)}&format=${format}`;

/** Downloads one register as a file through axios (keeps the JWT header). */
export const downloadExport = async (kind: GovKind, format: "xlsx" | "csv" = "xlsx") => {
  const res = await axiosInstance.get(`${BASE}/export`, {
    params: { kind, format },
    responseType: "blob",
  });
  const disposition: string = res.headers?.["content-disposition"] ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : `${kind.toLowerCase()}.${format}`;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Settings hook — one fetch per page load, shared by the Support page and the screens.
// ---------------------------------------------------------------------------

let settingsPromise: Promise<GovSettings> | null = null;
let settingsCache: GovSettings | null = null;

/** Fetches `/me` once per page load (re-fetched after a failure). */
export const loadGovernanceSettings = (): Promise<GovSettings> => {
  if (settingsCache) return Promise.resolve(settingsCache);
  if (!settingsPromise) {
    settingsPromise = getGovernanceSettings()
      .then((s) => {
        settingsCache = s;
        return s;
      })
      .catch((err) => {
        settingsPromise = null;
        throw err;
      });
  }
  return settingsPromise;
};

/** Forget the cached settings (call on logout or user switch). */
export const resetGovernanceSettings = () => {
  settingsPromise = null;
  settingsCache = null;
};

/**
 * `{ settings, loading, error }` — `settings` is `{enabled:false}` until
 * loaded (and after an error). The Support page (`Support/SupportHub`,
 * `SupportItemPage`) decides workbook vs ticket list from it.
 */
export const useGovernanceSettings = () => {
  const [settings, setSettings] = useState<GovSettings>(settingsCache ?? { enabled: false });
  const [loading, setLoading] = useState(!settingsCache);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    loadGovernanceSettings()
      .then((s) => {
        if (!alive) return;
        setSettings(s);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        setError(governanceErrorMessage(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  return { settings, loading, error };
};

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const governanceErrorStatus = (err: any): number | undefined => err?.response?.status;

export const governanceErrorMessage = (err: any): string => {
  const status = governanceErrorStatus(err);
  const serverMessage: string | undefined = err?.response?.data?.message;
  if (status === 429) return "Too many requests. Please wait a moment and try again.";
  if (status === 404) return serverMessage || "Not found.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 409) {
    return serverMessage || "Someone else changed this item first. Reload to see the new state.";
  }
  if (status === 503) return serverMessage || "Project governance is not configured.";
  if ((status === 400 || status === 403) && serverMessage) return serverMessage;
  if (status === 403) return "You do not have access to project governance.";
  if (status === 502 || status === undefined) {
    return "The ticket service is unavailable right now. Please try again later.";
  }
  return "Something went wrong. Please try again.";
};
